import styled from 'styled-components';
import theme from '../pagesv2/theme';

export const PageWrapper = styled.div`
  font-family: ${theme.typography.fontFamily};
  min-height: 100%;
`;

export const PageHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: ${theme.spacing.lg};
  gap: ${theme.spacing.md};
  flex-wrap: wrap;
`;

export const PageTitleGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.xs};
`;

export const PageTitle = styled.h1`
  font-size: ${theme.typography.h1.size};
  font-weight: ${theme.typography.h1.weight};
  color: ${theme.colors.text};
  margin: 0;
`;

export const PageSubtitle = styled.div`
  color: ${theme.colors.textSecondary};
  font-size: ${theme.typography.body.size};
`;

export const HeaderControls = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.md};
`;

export const FilterBar = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.md};
  flex-wrap: wrap;
  margin-bottom: ${theme.spacing.md};
`;

export const FilterPresets = styled.div`
  display: inline-flex;
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.radii.sm};
  overflow: hidden;
  background: ${theme.colors.surface};
`;

export const FilterPresetButton = styled.button`
  padding: 6px 12px;
  border: none;
  background: ${p => p.$active ? theme.colors.primary : 'transparent'};
  color: ${p => p.$active ? '#fff' : theme.colors.textSecondary};
  font-family: inherit;
  font-size: ${theme.typography.small.size};
  font-weight: 500;
  cursor: pointer;
  transition: background ${theme.transitions.fast}, color ${theme.transitions.fast};

  &:not(:last-child) {
    border-right: 1px solid ${theme.colors.border};
  }

  &:hover:not(:disabled) {
    background: ${p => p.$active ? theme.colors.primaryDark : theme.colors.surfaceHover};
  }
`;

export const DateRangeGroup = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: ${theme.typography.small.size};
  color: ${theme.colors.textSecondary};
`;

export const DateRangeLabel = styled.span`
  font-weight: 500;
`;

export const DateInput = styled.input`
  padding: 6px 8px;
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.radii.sm};
  background: ${theme.colors.surface};
  color: ${theme.colors.text};
  font-family: inherit;
  font-size: ${theme.typography.small.size};

  &:focus {
    outline: none;
    border-color: ${theme.colors.primary};
    box-shadow: 0 0 0 3px ${theme.colors.primaryGhost};
  }
`;

export const ClearDateButton = styled.button`
  padding: 4px 8px;
  border: none;
  background: transparent;
  color: ${theme.colors.textMuted};
  font-family: inherit;
  font-size: ${theme.typography.small.size};
  cursor: pointer;
  border-radius: ${theme.radii.sm};

  &:hover {
    background: ${theme.colors.surfaceHover};
    color: ${theme.colors.text};
  }
`;

export const SearchInput = styled.input`
  flex: 1;
  min-width: 200px;
  max-width: 360px;
  padding: 7px 12px;
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.radii.sm};
  background: ${theme.colors.surface};
  color: ${theme.colors.text};
  font-family: inherit;
  font-size: ${theme.typography.small.size};

  &::placeholder { color: ${theme.colors.textMuted}; }

  &:focus {
    outline: none;
    border-color: ${theme.colors.primary};
    box-shadow: 0 0 0 3px ${theme.colors.primaryGhost};
  }
`;

export const ResultCount = styled.span`
  font-size: ${theme.typography.small.size};
  color: ${theme.colors.textMuted};
  margin-left: auto;
`;

export const ErrorBanner = styled.div`
  padding: ${theme.spacing.sm} ${theme.spacing.md};
  background: ${theme.colors.dangerLight};
  border: 1px solid ${theme.colors.danger};
  border-radius: ${theme.radii.sm};
  color: ${theme.colors.danger};
  font-size: ${theme.typography.small.size};
  margin-bottom: ${theme.spacing.md};
`;

export const PrivacyNotice = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${theme.spacing.sm};
  padding: ${theme.spacing.md} ${theme.spacing.lg};
  background: ${theme.colors.primaryGhost};
  border: 1px solid ${theme.colors.primaryLight};
  border-radius: ${theme.radii.sm};
  color: ${theme.colors.text};
  font-size: ${theme.typography.small.size};
  line-height: 1.5;
  margin-bottom: ${theme.spacing.lg};

  code {
    background: ${theme.colors.borderLight};
    padding: 2px 6px;
    border-radius: ${theme.radii.sm};
    font-family: ${theme.typography.monoFamily || 'monospace'};
    font-size: ${theme.typography.tiny.size};
  }
`;

export const PrivacyNoticeIcon = styled.span`
  flex-shrink: 0;
  font-size: 16px;
  line-height: 1.5;
`;

/* Right-aligned switch for the Company-Admin "Recording notice" row.
   A real switch (not a bare checkbox) so the on/off state reads at a
   glance and matches the toggles used in the admin grid. */
export const ConsentToggleControl = styled.label`
  margin-left: auto;
  display: inline-flex;
  align-items: center;
  gap: ${theme.spacing.xs};
  flex-shrink: 0;
  cursor: pointer;
  font-weight: 600;
  color: ${theme.colors.textSecondary};
  white-space: nowrap;
`;

export const ConsentToggleSwitch = styled.span`
  position: relative;
  display: inline-block;
  width: 40px;
  height: 22px;

  input {
    opacity: 0;
    width: 0;
    height: 0;
    position: absolute;
  }

  span {
    position: absolute;
    inset: 0;
    background: ${theme.colors.border};
    border-radius: ${theme.radii.full};
    transition: background 0.15s ease;
  }

  span::before {
    content: '';
    position: absolute;
    height: 16px;
    width: 16px;
    left: 3px;
    top: 3px;
    background: #fff;
    border-radius: 50%;
    transition: transform 0.15s ease;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.25);
  }

  input:checked + span {
    background: ${theme.colors.primary};
  }

  input:checked + span::before {
    transform: translateX(18px);
  }

  input:disabled + span {
    opacity: 0.5;
  }
`;

export const EmptyState = styled.div`
  padding: ${theme.spacing.xl};
  text-align: center;
  background: ${theme.colors.surface};
  border-radius: ${theme.radii.md};
  border: 1px solid ${theme.colors.border};
  color: ${theme.colors.textSecondary};
  font-size: ${theme.typography.body.size};
  line-height: 1.6;
`;

export const EmptyStateTitle = styled.div`
  font-size: ${theme.typography.h3.size};
  font-weight: ${theme.typography.h3.weight};
  color: ${theme.colors.text};
  margin-bottom: ${theme.spacing.sm};
`;

export const EmptyStateCode = styled.code`
  background: ${theme.colors.borderLight};
  padding: 2px 6px;
  border-radius: ${theme.radii.sm};
  font-size: 13px;
  color: ${theme.colors.primary};
`;

export const MeetingsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.sm};
`;

export const Card = styled.div`
  background: ${theme.colors.surface};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.radii.md};
  overflow: hidden;
  transition: box-shadow ${theme.transitions.fast};

  &:hover {
    box-shadow: ${theme.shadows.sm};
  }
`;

export const CardHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.md};
  padding: ${theme.spacing.md};
  cursor: pointer;
  user-select: none;

  &:hover {
    background: ${theme.colors.surfaceHover};
  }
`;

export const Chevron = styled.span`
  color: ${theme.colors.textMuted};
  font-size: 14px;
  display: inline-block;
  transition: transform ${theme.transitions.fast};
  transform: rotate(${p => p.$expanded ? '90deg' : '0deg'});
  width: 12px;
  flex-shrink: 0;
`;

export const CardSummary = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
`;

export const CardTitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
  flex-wrap: wrap;
`;

export const CardTitle = styled.span`
  font-size: ${theme.typography.body.size};
  font-weight: 600;
  color: ${theme.colors.text};
`;

export const CardMeta = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
  font-size: ${theme.typography.small.size};
  color: ${theme.colors.textSecondary};
  flex-wrap: wrap;
`;

export const MetaItem = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
`;

export const MetaSeparator = styled.span`
  color: ${theme.colors.border};
`;

export const CardActions = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
  flex-shrink: 0;
`;

export const CardBody = styled.div`
  padding: 0 ${theme.spacing.lg} ${theme.spacing.lg} ${theme.spacing.lg};
  border-top: 1px solid ${theme.colors.borderLight};
`;

export const StatusBanner = styled.div`
  margin-top: ${theme.spacing.md};
  padding: ${theme.spacing.sm} ${theme.spacing.md};
  border-radius: ${theme.radii.sm};
  font-size: ${theme.typography.small.size};
  background: ${p => p.$variant === 'failed' ? theme.colors.dangerLight : theme.colors.infoLight};
  color: ${p => p.$variant === 'failed' ? theme.colors.danger : theme.colors.info};
  border: 1px solid ${p => p.$variant === 'failed' ? theme.colors.danger : theme.colors.info};
`;

export const Section = styled.section`
  margin-top: ${theme.spacing.lg};
`;

export const SectionTitle = styled.h3`
  font-size: ${theme.typography.h3.size};
  font-weight: ${theme.typography.h3.weight};
  color: ${theme.colors.text};
  margin: 0 0 ${theme.spacing.sm} 0;
  padding-bottom: ${theme.spacing.xs};
  border-bottom: 1px solid ${theme.colors.borderLight};
`;

export const SectionContent = styled.div`
  font-size: ${theme.typography.body.size};
  color: ${theme.colors.text};
  line-height: 1.6;
`;

export const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: ${theme.spacing.md};
  margin-top: ${theme.spacing.sm};
`;

export const InfoField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

export const InfoLabel = styled.span`
  font-size: ${theme.typography.tiny.size};
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: ${theme.colors.textMuted};
  font-weight: 500;
`;

export const InfoValue = styled.span`
  font-size: ${theme.typography.body.size};
  color: ${theme.colors.text};
`;

export const ParticipantList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${theme.spacing.xs};
  margin-top: ${theme.spacing.xs};
`;

export const ParticipantChip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  background: ${p => p.$isMultiplyUser ? theme.colors.primaryGhost : theme.colors.borderLight};
  color: ${p => p.$isMultiplyUser ? theme.colors.primary : theme.colors.textSecondary};
  border-radius: ${theme.radii.full};
  font-size: ${theme.typography.small.size};
  font-weight: 500;
`;

export const SalesPersonChip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 8px;
  background: ${theme.colors.borderLight};
  color: ${theme.colors.textSecondary};
  border-radius: ${theme.radii.sm};
  font-size: ${theme.typography.tiny.size};
  font-weight: 500;
`;

export const ListBlock = styled.ul`
  margin: ${theme.spacing.xs} 0 0 0;
  padding-left: ${theme.spacing.lg};
  font-size: ${theme.typography.body.size};
  color: ${theme.colors.text};
  line-height: 1.6;
  & li { margin-bottom: ${theme.spacing.xs}; }
`;

export const Placeholder = styled.div`
  font-size: ${theme.typography.small.size};
  color: ${theme.colors.textMuted};
  font-style: italic;
  padding: ${theme.spacing.sm} 0;
`;

export const ToggleButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 14px;
  border-radius: ${theme.radii.sm};
  border: 1px solid ${theme.colors.border};
  background: ${theme.colors.surface};
  color: ${theme.colors.textSecondary};
  font-family: inherit;
  font-size: ${theme.typography.small.size};
  font-weight: 500;
  cursor: pointer;
  transition: all ${theme.transitions.fast};

  &:hover:not(:disabled) {
    background: ${theme.colors.surfaceHover};
    border-color: ${theme.colors.primary};
    color: ${theme.colors.primary};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

export const TranscriptBlock = styled.div`
  margin-top: ${theme.spacing.sm};
  padding: ${theme.spacing.md};
  background: ${theme.colors.background};
  border-radius: ${theme.radii.sm};
  border: 1px solid ${theme.colors.borderLight};
  max-height: 400px;
  overflow-y: auto;
  font-size: ${theme.typography.small.size};
  line-height: 1.6;
`;

export const TranscriptSegment = styled.div`
  margin-bottom: ${theme.spacing.sm};
  &:last-child { margin-bottom: 0; }
`;

export const TranscriptSpeaker = styled.span`
  font-weight: 600;
  color: ${theme.colors.primary};
  margin-right: 6px;
`;

// ── Problem Alignment ─────────────────────────────────────────────────────────

export const AlignmentSection = styled.section`
  margin-top: ${theme.spacing.lg};
  padding: ${theme.spacing.md};
  background: ${theme.colors.primaryGhost};
  border: 1px solid ${theme.colors.primary};
  border-radius: ${theme.radii.md};
`;

export const AlignmentHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
  margin-bottom: ${theme.spacing.sm};
`;

export const AlignmentTitle = styled.h3`
  font-size: ${theme.typography.h3.size};
  font-weight: ${theme.typography.h3.weight};
  color: ${theme.colors.text};
  margin: 0;
`;

export const FitLevelBadge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 4px 12px;
  border-radius: ${theme.radii.full};
  font-size: ${theme.typography.tiny.size};
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  background: ${p => p.$bg};
  color: ${p => p.$color};
  border: 1px solid ${p => p.$border};
`;

export const AlignmentSummary = styled.div`
  font-size: ${theme.typography.body.size};
  color: ${theme.colors.text};
  line-height: 1.5;
  margin-bottom: ${theme.spacing.md};
`;

export const MatchCard = styled.div`
  padding: ${theme.spacing.md};
  background: ${theme.colors.surface};
  border: 1px solid ${theme.colors.border};
  border-left: 4px solid ${theme.colors.success};
  border-radius: ${theme.radii.sm};
  margin-bottom: ${theme.spacing.sm};
  &:last-child { margin-bottom: 0; }
`;

export const MatchProblem = styled.div`
  font-size: ${theme.typography.body.size};
  font-weight: 600;
  color: ${theme.colors.text};
  line-height: 1.4;
  margin-bottom: ${theme.spacing.sm};
`;

export const QuoteBlock = styled.blockquote`
  position: relative;
  margin: ${theme.spacing.sm} 0 ${theme.spacing.md} 0;
  padding: 14px 18px 14px 22px;
  background: ${theme.colors.primaryGhost};
  border-left: 4px solid ${theme.colors.primary};
  border-radius: ${theme.radii.sm};
  font-size: ${theme.typography.body.size};
  color: ${theme.colors.text};
  line-height: 1.55;
  font-style: italic;
  font-weight: 500;
  box-shadow: 0 1px 2px rgba(15, 30, 50, 0.04);
`;

export const QuoteAttribution = styled.div`
  margin-top: 8px;
  font-size: ${theme.typography.small.size};
  color: ${theme.colors.primaryDark};
  font-style: normal;
  font-weight: 600;
  letter-spacing: 0.2px;
`;

export const TagRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: ${theme.spacing.sm};
`;

export const SmallTag = styled.span`
  display: inline-block;
  font-size: ${theme.typography.tiny.size};
  text-transform: uppercase;
  letter-spacing: 0.04em;
  padding: 2px 8px;
  border-radius: ${theme.radii.full};
  background: ${p => p.$bg || theme.colors.borderLight};
  color: ${p => p.$color || theme.colors.textSecondary};
  font-weight: 600;
`;

export const SalesActionRow = styled.div`
  font-size: ${theme.typography.small.size};
  color: ${theme.colors.text};
  line-height: 1.5;
  & > strong { color: ${theme.colors.primary}; }
`;

export const AlignmentEmptyState = styled.div`
  font-size: ${theme.typography.small.size};
  color: ${theme.colors.textSecondary};
  font-style: italic;
  padding: ${theme.spacing.sm} 0;
`;

// ── Prospect Profile ──────────────────────────────────────────────────────────

export const ProspectProfileGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${theme.spacing.lg};
  ${theme.media.tablet} { grid-template-columns: 1fr; }
`;

export const ProfileColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.md};
`;

export const ProfileGroup = styled.div``;

export const ProfileGroupLabel = styled.div`
  font-size: ${theme.typography.tiny.size};
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: ${theme.colors.textMuted};
  font-weight: 600;
  margin-bottom: 6px;
`;

export const FitFooter = styled.div`
  margin-top: ${theme.spacing.md};
  padding-top: ${theme.spacing.sm};
  border-top: 1px solid ${theme.colors.borderLight};
  font-size: ${theme.typography.small.size};
  color: ${theme.colors.text};
  line-height: 1.5;
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.xs};
`;

export const FitFooterRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
`;
